import {
  CheckCircle2,
  DollarSign,
  Eye,
  Lock,
  MapPin,
  Send,
  Truck,
  Zap,
} from "lucide-react";
import { motion } from "framer-motion";
import dayjs from "dayjs";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useConnection, useWriteContract } from "wagmi";
import { useQueryClient } from "@tanstack/react-query";
import {
  simulateContract,
  switchChain,
  waitForTransactionReceipt,
} from "@wagmi/core";
import { wagmiConfig } from "@/config";
import { FORTTRADE_CONTRACT_ADDRESS } from "@/config/env";
import { FortTradeAbi } from "@/abi/FortTrade.abi";
import { FortAgentActivateResultCode, type MyFortAgentItem } from "@/api/types";
import { showError, showInfo, showSuccess } from "@/lib/toast";
import { parseChainError } from "@/lib/chainError";
import { useFortAgentActivateSign } from "@/hooks/api/useFortAgentActivateSign";
import { useMyAgentsUiStore } from "@/store/useMyAgentsUiStore";
import { getAgentTierKey, getTokenName } from "@/config/common";
import { C } from "@/components/modal/home";
import { CCC_FUSD_ADDRESS } from "@/config/env";
import { formatEtherWithDecimals, formatThousands } from "@/lib/utils";
import { bscChainId } from "@/config/wagmi";
import { AGENT_IMAGE_URL_INACTIVE } from "./constants";

export type MyAgentInactiveCardV2Props = {
  agent: MyFortAgentItem;
  onTransfer: () => void;
  hasActivatedAgent: boolean;
};

export function MyAgentInactiveCardV2({
  agent,
  onTransfer,
  hasActivatedAgent,
}: MyAgentInactiveCardV2Props) {
  const { t } = useTranslation();
  const { address } = useConnection();
  const writeContract = useWriteContract();
  const { mutateAsync: activateSignAsync } = useFortAgentActivateSign();
  const [activating, setActivating] = useState(false);
  const { openInactiveDetail, openPayFusd, payFusdAgentId } =
    useMyAgentsUiStore();
  const queryClient = useQueryClient();

  const showNotActivatedTip = () => {
    showInfo(t("homePage.myAgentCard.notActivatedTip"));
  };

  const tierLabel = t(getAgentTierKey(agent.name));
  const tagColor = C.amber;
  const fusdTokenName = getTokenName(CCC_FUSD_ADDRESS);

  const mustPayFusdBeforeActivate =
    agent.paymentMode === "mixed" &&
    !agent.fusdPurchaseDeducted &&
    BigInt(agent.purchasePayFusdWei || "0") > 0n;

  const fusdOwedDisplay = formatThousands(
    formatEtherWithDecimals(agent.purchasePayFusdWei, 2),
    2
  );

  const handleActivate = async () => {
    if (!address) {
      showError(t("toast.common.connectWalletFirst"));
      return;
    }
    await switchChain(wagmiConfig, { chainId: bscChainId });
    setActivating(true);
    try {
      const res = await activateSignAsync({ agentId: agent.agentId });
      if (res.resultCode !== FortAgentActivateResultCode.Success) return;

      const { request } = await simulateContract(wagmiConfig, {
        address: FORTTRADE_CONTRACT_ADDRESS as `0x${string}`,
        abi: FortTradeAbi,
        functionName: "activate",
        args: [
          BigInt(res.agentId),
          BigInt(res.deadline),
          BigInt(res.nonce),
          res.signature as `0x${string}`,
        ],
      });
      const hash = await writeContract.mutateAsync(request);
      await waitForTransactionReceipt(wagmiConfig, { hash });
      showSuccess(t("toast.fortAgent.activate.result.success"));
      queryClient.invalidateQueries({ queryKey: ["myFortAgents"] });
    } catch (err) {
      console.error("activateErr", err);
      const errorKey = parseChainError(err);
      showError(t(errorKey));
    } finally {
      setActivating(false);
    }
  };

  const onActivateClick = () => {
    if (!address) {
      showError(t("toast.common.connectWalletFirst"));
      return;
    }
    if (mustPayFusdBeforeActivate) {
      openPayFusd(agent.agentId);
      return;
    }
    void handleActivate();
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl overflow-hidden"
      style={{
        background: `linear-gradient(135deg, ${tagColor}04 0%, ${C.card} 40%, ${C.card2} 100%)`,
        border: `1px solid ${tagColor}15`,
      }}
    >
      <div className="p-4 pb-3">
        <div className="flex items-center gap-3">
          <div
            className="w-14 h-14 rounded-xl overflow-hidden shrink-0 flex items-center justify-center"
            style={{
              background: "rgba(255,255,255,0.03)",
              border: `1px solid ${tagColor}10`,
            }}
          >
            <img
              src={AGENT_IMAGE_URL_INACTIVE}
              alt=""
              className="w-full h-full object-contain"
              style={{ opacity: 0.55 }}
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-base font-bold" style={{ color: C.muted }}>
                {agent.name}
              </span>
              <span
                className="text-[9px] px-1.5 py-0.5 rounded-full font-bold"
                style={{
                  background: `${tagColor}15`,
                  color: tagColor,
                  border: `1px solid ${tagColor}20`,
                }}
              >
                {tierLabel}
              </span>
            </div>
            <div className="text-[10px] mt-0.5" style={{ color: C.dim }}>
              {t("homePage.myAgentCard.purchasedOnShort", {
                date: dayjs(agent.createdAt).format("YYYY-MM-DD"),
              })}
            </div>
          </div>
          <span
            className="text-[10px] px-2.5 py-1 rounded-full font-bold shrink-0 flex items-center gap-1"
            style={{
              background: "rgba(212,165,116,0.08)",
              color: "#d4a574",
              border: "1px solid rgba(212,165,116,0.12)",
            }}
          >
            <Lock size={8} />
            {t("homePage.myAgentCard.statusPending")}
          </span>
        </div>

        {/* {mustPayFusdBeforeActivate ? (
          <div
            className="mt-3 flex items-center gap-1.5 rounded-lg px-2.5 py-2"
            style={{
              background: "rgba(239,68,68,0.04)",
              border: "1px solid rgba(239,68,68,0.10)",
            }}
          >
            <DollarSign size={12} style={{ color: C.red }} />
            <span
              className="text-[11px] font-semibold"
              style={{ color: C.red }}
            >
              {t("homePage.myAgentInactiveDetailDialog.pendingFusdNotice", {
                amount: fusdOwedDisplay,
                tokenName: fusdTokenName,
              })}
            </span>
          </div>
        ) : (
          <div
            className="mt-3 rounded-lg px-2.5 py-2 flex items-center gap-2"
            style={{
              background: "rgba(34,197,94,0.04)",
              border: `1px solid ${C.primary}10`,
            }}
          >
            <CheckCircle2 size={12} style={{ color: C.primary }} />
            <span className="text-[10px]" style={{ color: C.muted }}>
              {hasActivatedAgent
                ? t("homePage.myAgentCard.fullPaidReadyTransfer")
                : t("homePage.myAgentCard.fullPaidReadyActivate")}
            </span>
          </div>
        )} */}
      </div>

      <div className="px-4 pb-3">
        <div className="grid grid-cols-4 gap-2">
          <button
            type="button"
            className="flex flex-col items-center gap-1 py-2 rounded-xl transition-all active:scale-95"
            style={{
              background: `${C.primary}06`,
              border: `1px solid ${C.primary}08`,
            }}
            onClick={showNotActivatedTip}
          >
            <MapPin size={14} style={{ color: C.primary }} />
            <span
              className="text-[9px] font-medium"
              style={{ color: C.primary }}
            >
              {t("homePage.myAgentCard.deliveryAddress")}
            </span>
          </button>
          <button
            type="button"
            className="flex flex-col items-center gap-1 py-2 rounded-xl transition-all active:scale-95"
            style={{
              background: `${C.cyan}06`,
              border: `1px solid ${C.cyan}08`,
            }}
            onClick={showNotActivatedTip}
          >
            <Truck size={14} style={{ color: C.cyan }} />
            <span className="text-[9px] font-medium" style={{ color: C.cyan }}>
              {t("homePage.myAgentCard.deliveryStatus")}
            </span>
          </button>
          <button
            type="button"
            className="flex flex-col items-center gap-1 py-2 rounded-xl transition-all active:scale-95"
            style={{
              background: `${C.amber}06`,
              border: `1px solid ${C.amber}08`,
            }}
            onClick={onTransfer}
          >
            <Send size={14} style={{ color: C.amber }} />
            <span className="text-[9px] font-medium" style={{ color: C.amber }}>
              {t("homePage.myAgentCard.transfer")}
            </span>
          </button>
          <button
            type="button"
            className="flex flex-col items-center gap-1 py-2 rounded-xl transition-all active:scale-95"
            style={{
              background: `${C.muted}06`,
              border: `1px solid rgba(136,146,168,0.12)`,
            }}
            onClick={() => openInactiveDetail(agent.agentId)}
          >
            <Eye size={14} style={{ color: C.muted }} />
            <span className="text-[9px] font-medium" style={{ color: C.muted }}>
              {t("homePage.myAgentCard.viewDetail")}
            </span>
          </button>
        </div>
      </div>

      <div className="px-4 pb-4">
        {!hasActivatedAgent ? (
          <button
            type="button"
            className="w-full py-2.5 rounded-xl text-[11px] font-bold flex items-center justify-center gap-1.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.99]"
            style={
              mustPayFusdBeforeActivate
                ? {
                    background: `${C.red}10`,
                    color: C.red,
                    border: `1px solid ${C.red}20`,
                  }
                : {
                    background: `${C.primary}10`,
                    color: C.primary,
                    border: `1px solid ${C.primary}20`,
                  }
            }
            onClick={onActivateClick}
            disabled={activating || payFusdAgentId === agent.agentId}
          >
            {activating ? (
              t("homePage.myAgentCard.activating")
            ) : mustPayFusdBeforeActivate ? (
              <>
                <DollarSign size={12} />
                {t("homePage.myAgentCard.payAndActivate")}
              </>
            ) : (
              <>
                <Zap size={12} />
                {t("homePage.myAgentCard.activate")}
              </>
            )}
          </button>
        ) : (
          <div
            className="w-full py-2.5 rounded-xl text-[10px] font-medium flex items-center justify-center gap-1.5"
            style={{
              background: `${C.muted}06`,
              border: `1px solid rgba(136,146,168,0.12)`,
              color: C.muted,
            }}
          >
            <Lock size={12} className="shrink-0" />
            <span>
              {t("homePage.myAgentCard.cannotActivateWhileOtherActive")}
            </span>
          </div>
        )}
      </div>
    </motion.div>
  );
}
